import type { ReactNode } from 'react';
import { useState } from 'react';
import { ArrowRight, Mail, Lock, User } from 'lucide-react';
import { useRouter } from '../router/MarketplaceRouter';
import { Button } from '../shell/ui';

function AuthShell({
  side,
  children,
}: {
  side: 'signin' | 'signup';
  children: ReactNode;
}) {
  return (
    <section
      style={{
        minHeight: 'calc(100vh - 80px)',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
      }}
      className="auth-shell"
    >
      {/* Left: form */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4rem 2rem',
          background: 'var(--color-background-primary)',
        }}
      >
        <div style={{ width: '100%', maxWidth: '440px' }}>{children}</div>
      </div>

      {/* Right: editorial image */}
      <div
        style={{
          position: 'relative',
          background:
            side === 'signup'
              ? 'url(https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=1400&q=80) center/cover no-repeat'
              : 'url(https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=80) center/cover no-repeat',
        }}
        className="auth-image"
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(28, 27, 26, 0.05) 0%, rgba(28, 27, 26, 0.55) 100%)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            padding: '4rem 3rem',
            color: 'var(--color-text-inverse)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          <blockquote
            style={{
              fontFamily: 'var(--font-family-display)',
              fontStyle: 'italic',
              fontSize: 'clamp(1.375rem, 2vw, 1.75rem)',
              lineHeight: 1.35,
              margin: 0,
              marginBottom: '1.25rem',
              maxWidth: '480px',
            }}
          >
            “Atelier Nord delivered exactly what the drawings promised. Weekly updates were calm and clear. The kitchen is the quietest room of the apartment now.”
          </blockquote>
          <div style={{ fontSize: '0.875rem', opacity: 0.85 }}>
            Aygun M. · Icherisheher duplex renovation
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 980px) {
          .auth-shell { grid-template-columns: 1fr !important; }
          .auth-image { display: none; }
        }
      `}</style>
    </section>
  );
}

const inputWrap: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem',
  padding: '0.75rem 1rem',
  background: 'var(--color-background-card)',
  border: '1px solid var(--color-border-medium)',
  borderRadius: 'var(--radius-md)',
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '0.9375rem',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-body)',
};

export function SignInScreen() {
  const { navigate } = useRouter();
  return (
    <AuthShell side="signin">
      <div
        style={{
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          color: 'var(--color-accent-rosegold)',
          fontWeight: 500,
          marginBottom: '0.875rem',
        }}
      >
        Welcome back
      </div>
      <h1
        style={{
          fontFamily: 'var(--font-family-display)',
          fontSize: '2.5rem',
          fontWeight: 400,
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
          margin: 0,
          marginBottom: '0.75rem',
        }}
      >
        Sign in.
      </h1>
      <p style={{ color: 'var(--color-text-secondary)', marginBottom: '2.25rem' }}>
        Pick up where you left off.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          navigate('dashboard');
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}
      >
        <div style={inputWrap}>
          <Mail size={16} style={{ color: 'var(--color-text-muted)' }} />
          <input type="email" placeholder="Email address" style={inputStyle} />
        </div>
        <div style={inputWrap}>
          <Lock size={16} style={{ color: 'var(--color-text-muted)' }} />
          <input type="password" placeholder="Password" style={inputStyle} />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.8125rem',
            color: 'var(--color-text-secondary)',
          }}
        >
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
            <input type="checkbox" /> Keep me signed in
          </label>
          <a style={{ color: 'var(--color-accent-rosegold)', cursor: 'pointer' }}>Forgot password?</a>
        </div>
        <Button type="submit" size="lg" style={{ marginTop: '0.5rem' }} rightIcon={<ArrowRight size={16} />}>
          Sign in
        </Button>
      </form>

      <div
        style={{
          marginTop: '2rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid var(--color-border-light)',
          textAlign: 'center',
          fontSize: '0.875rem',
          color: 'var(--color-text-secondary)',
        }}
      >
        New to Baza?{' '}
        <button
          onClick={() => navigate('signup')}
          style={{
            all: 'unset',
            cursor: 'pointer',
            color: 'var(--color-accent-rosegold)',
            fontWeight: 500,
          }}
        >
          Create an account
        </button>
      </div>
    </AuthShell>
  );
}

export function SignUpScreen() {
  const { navigate } = useRouter();
  const [userType, setUserType] = useState<'customer' | 'vendor'>('customer');

  return (
    <AuthShell side="signup">
      <div
        style={{
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          color: 'var(--color-accent-rosegold)',
          fontWeight: 500,
          marginBottom: '0.875rem',
        }}
      >
        Create an account
      </div>
      <h1
        style={{
          fontFamily: 'var(--font-family-display)',
          fontSize: '2.5rem',
          fontWeight: 400,
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
          margin: 0,
          marginBottom: '0.75rem',
        }}
      >
        Join <span style={{ color: 'var(--color-accent-rosegold)' }}>Baza</span>.
      </h1>
      <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.75rem' }}>
        Two minutes, no card required.
      </p>

      {/* Role toggle */}
      <div
        style={{
          display: 'flex',
          gap: '0.375rem',
          padding: '0.25rem',
          background: 'var(--color-background-surface)',
          borderRadius: 'var(--radius-md)',
          marginBottom: '1.75rem',
        }}
      >
        {(['customer', 'vendor'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setUserType(t)}
            style={{
              all: 'unset',
              cursor: 'pointer',
              flex: 1,
              textAlign: 'center',
              padding: '0.625rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: 'var(--radius-sm)',
              background: userType === t ? 'var(--color-background-card)' : 'transparent',
              color: userType === t ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
              boxShadow: userType === t ? 'var(--shadow-subtle)' : 'none',
              textTransform: 'capitalize',
            }}
          >
            I’m a {t}
          </button>
        ))}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          navigate('dashboard');
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}
      >
        <div style={inputWrap}>
          <User size={16} style={{ color: 'var(--color-text-muted)' }} />
          <input
            type="text"
            placeholder={userType === 'customer' ? 'Full name' : 'Studio or business name'}
            style={inputStyle}
          />
        </div>
        <div style={inputWrap}>
          <Mail size={16} style={{ color: 'var(--color-text-muted)' }} />
          <input type="email" placeholder="Email address" style={inputStyle} />
        </div>
        <div style={inputWrap}>
          <Lock size={16} style={{ color: 'var(--color-text-muted)' }} />
          <input type="password" placeholder="Password" style={inputStyle} />
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          By creating an account you agree to our Terms and Privacy policy.
        </div>
        <Button type="submit" size="lg" style={{ marginTop: '0.25rem' }} rightIcon={<ArrowRight size={16} />}>
          Create account
        </Button>
      </form>

      <div
        style={{
          marginTop: '2rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid var(--color-border-light)',
          textAlign: 'center',
          fontSize: '0.875rem',
          color: 'var(--color-text-secondary)',
        }}
      >
        Already have an account?{' '}
        <button
          onClick={() => navigate('signin')}
          style={{
            all: 'unset',
            cursor: 'pointer',
            color: 'var(--color-accent-rosegold)',
            fontWeight: 500,
          }}
        >
          Sign in
        </button>
      </div>
    </AuthShell>
  );
}
